<template>
  <v-container py-0>
    <v-progress-linear
      :active="loading"
      height="3"
      :indeterminate="true"
      class="my-1"
    />
    <v-card color="transparent" flat light>
      <v-card-text class="py-0">
        <v-layout wrap row>
          <v-flex xs12 md6 px-3>
            <v-layout wrap row>
              <v-flex xs12 px-2>
                <v-text-field
                  v-model="stadium.name"
                  v-validate="'required'"
                  :label="$t('fields.global.name') + ' (*)'"
                  :data-vv-name="$t('fields.global.name')"
                  :error-messages="errors.collect($t('fields.global.name'))"
                />
              </v-flex>
              <v-flex xs12 px-2>
                <v-text-field
                  v-model="stadium.address"
                  :label="$t('fields.users.address')"
                  autocomplete="new-text"
                  browser-autocomplete
                />
              </v-flex>
              <v-flex xs12 px-2>
                <v-text-field
                  v-model="stadium.cp"
                  :label="$t('fields.users.cp')"
                  autocomplete="new-text"
                  browser-autocomplete
                />
              </v-flex>
              <v-flex xs12 px-2>
                <CountryField
                  v-model="stadium.countryId"
                  clearable
                  :label="
                    $t('action.actions.select', {
                      field: $tc('models.country'),
                    })
                  "
                  autocomplete="new-text"
                  browser-autocomplete
                />
              </v-flex>
              <v-flex xs12 px-2>
                <RegionField
                  v-model="stadium.regionId"
                  :disabled="!stadium.countryId"
                  :country-id="stadium.countryId"
                  clearable
                  :label="
                    $t('action.actions.select', { field: $tc('models.region') })
                  "
                  autocomplete="new-text"
                  browser-autocomplete
                />
              </v-flex>
              <v-flex xs12 px-2>
                <ProvinceField
                  v-model="stadium.provinceId"
                  :disabled="!stadium.regionId"
                  :region-id="stadium.regionId"
                  clearable
                  :label="
                    $t('action.actions.select', {
                      field: $tc('models.province'),
                    })
                  "
                  autocomplete="new-text"
                  browser-autocomplete
                />
              </v-flex>
              <v-flex xs12 px-2>
                <TownField
                  v-model="stadium.townId"
                  :disabled="!stadium.provinceId"
                  :province-id="stadium.provinceId"
                  :label="
                    $t('action.actions.select', { field: $tc('models.town') })
                  "
                  autocomplete="new-text"
                  browser-autocomplete
                />
              </v-flex>
              <v-flex xs12 px-2>
                <v-text-field
                  v-model="titleFile"
                  :label="$t('fields.global.logo')"
                  disabled
                  prepend-icon="attach_file"
                />
                <upload-button
                  :file-changed-callback="fileChanged"
                  small
                  :title="titleUpload"
                />
              </v-flex>
              <v-flex v-if="image" md4 sm6 xs12 px-2>
                <div style="max-width: 150px">
                  <clipper-preview class="basic md" name="preview" />
                </div>
              </v-flex>
              <v-flex v-if="image" md4 sm6 xs12 px-2>
                <div style="max-width: 150px">
                  <clipper-basic
                    :src="image"
                    class="basic lg"
                    preview="preview"
                  />
                </div>
              </v-flex>
              <v-flex xs12 px-2>
                <v-switch
                  v-model="stadium.active"
                  :label="$t('fields.global.active')"
                  color="primary"
                />
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs12 md6 px-3>
            <v-layout wrap row>
              <v-flex d-flex xs12 px-2>
                <geo-map
                  ref="geoMap"
                  :location="locationForMap"
                  @location="updateLocation"
                />
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <span style="color: red">(*) &nbsp;</span>
        <span>{{ $t('action.messages.fieldRequired') }}</span>
        <v-spacer />
        <meta-button :disabled="loading" type="reset" @click="clear" />
        <meta-button
          v-if="stadium._id"
          :loading="loading"
          type="update"
          @click="submit"
        />
        <meta-button v-else :loading="loading" type="create" @click="submit" />
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { clipperBasic, clipperPreview } from 'vuejs-clipper'
import UploadButton from 'vuetify-upload-button'
import { mapActions, mapState } from 'vuex'
import CountryField from '@/components/widgets/Input/CountryField'
import ProvinceField from '@/components/widgets/Input/ProvinceField'
import RegionField from '@/components/widgets/Input/RegionField'
import TownField from '@/components/widgets/Input/TownField'
import GeoMap from '@/components/widgets/maps/GeoMaps'
import queueToast from '@/helpers/queueToast'
import { parseObject } from '@/helpers/utils'

export default {
  name: 'TabInformation',
  components: {
    GeoMap,
    UploadButton,
    clipperBasic,
    clipperPreview,
    CountryField,
    RegionField,
    ProvinceField,
    TownField,
  },
  data() {
    return {
      stadium: {},
      image: '',
      file: '',
      titleFile: '',
    }
  },
  watch: {
    selected: {
      handler: function (val) {
        this.setObject(val)
      },
      immediate: true,
      deep: true,
    },
    'stadium.photo': {
      handler: function (val) {
        if (val) {
          if (val.includes('http')) this.image = val
          else this.image = this.defaultValues.imageUrl + val
        } else {
          this.image = ''
        }
        this.titleFile = val
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.setObject(this.selected)
  },
  computed: {
    ...mapState({
      selected: (state) => state.stadiums.selected,
      loading: (state) => state.stadiums.loading,
      listLanguages: (state) => state.languages.listSelectLanguage,
    }),
    locationForMap() {
      let result = ''
      // const { address, townId, provinceId, countryId } = this.stadium
      // if (address) {
      //   result += address + ', '
      // }
      // if (townId) {
      //   result += this.findLocation(townId, 'town') + ', '
      // }

      // if (provinceId) {
      //   result += this.findLocation(provinceId, 'province') + ', '
      // }

      // if (countryId) {
      //   result += this.findLocation(countryId, 'country') + ', '
      // }

      return result
    },
    titleUpload() {
      return this.image
        ? this.$t('action.actions.change', {
            field: this.$t('fields.users.image'),
          })
        : this.$t('action.actions.upload', {
            field: this.$t('fields.users.image'),
          })
    },
  },
  methods: {
    ...mapActions({
      setCreate: 'stadiums/create',
      setUpdate: 'stadiums/update',
      reset: 'stadiums/reset',
      uploadFile: 'files/uploadPush',
    }),
    submit() {
      this.validate().then((valid) => {
        if (!valid) return
        let payload = Object.assign({}, this.stadium)

        if (!payload._id) {
          this.setCreate(payload).then((item) => {
            if (this.file) {
              this.uploadFile({
                id: item._id,
                model: 'Stadiums',
                field: 'photo',
                file: this.file,
              })
            }
            this.$emit('close')
            this.clear()
          })
        } else {
          this.setUpdate(payload).then((item) => {
            if (this.file) {
              this.uploadFile({
                id: item._id,
                model: 'Stadiums',
                field: 'photo',
                file: this.file,
              })
            }
            this.$emit('close')
            this.clear()
          })
        }
      })
    },
    updateLocation(item) {
      this.stadium.lat = item.lat
      this.stadium.long = item.lng
      queueToast('Agregada la direccion correctamente', 'success')
    },
    findLocation(/* id, type */) {
      return ''
      // switch (type) {
      //   case 'country':
      //     const country = this.countrySelectList.find(item => item.value === id)
      //     return country ? country.text : ''
      //   case 'province':
      //     const province = this.provinceSelectList.find(
      //       item => item.value === id
      //     )
      //     return province ? province.text : ''
      //   case 'town':
      //     const town = this.townSelectList.find(item => item.value === id)
      //     return town ? town.text : ''
      //   default:
      //     return ''
      // }
    },
    clear() {
      this.reset()
      this.$validator.reset()
      this.$nextTick(() => {
        this.$refs.geoMap.searchResult = []
        this.$refs.geoMap.marks = []
      })
    },
    setObject(stadium) {
      this.$nextTick(() => {
        if (this.$refs.geoMap) {
          this.$refs.geoMap.$refs.myMap.mapObject.invalidateSize()
        }

        if (stadium) {
          this.stadium = parseObject(stadium)

          if (stadium.lat && stadium.lng) {
            this.$refs.geoMap.clickMark({
              latlng: {
                lat: this.stadium.lat,
                lng: this.stadium.long,
              },
            })
          } else this.$refs.geoMap.autoCenter()
        }
      })
    },

    fileChanged(file) {
      if (file.size < 1) return
      this.file = file
      this.titleFile = file.name
      const reader = new FileReader()
      const vm = this

      reader.onload = (e) => {
        vm.image = e.target.result
      }
      reader.readAsDataURL(file)
    },
  },
}
</script>
